import { useState } from "react";
import styled from "styled-components";
import { BBOT_SUPPORT_EMAIL } from "Constants";
import { BbotTable, notification, Switch } from "top-component-library";
import { saveSupportAlerts } from "util/ApiCalls";
import { capitalizeString, splitSnakeCase } from "util/Utils";

// Admin-only panel for internal DoorDash staff (SupportStaff with is_customer=false). Admins
// subscribe a recipient to the recommended preset admin alerts or unsubscribe them; the backend
// owns the preset contents (usePresetAdminAlerts), and unsubscribing clears the recipient's alerts
// via an empty customAlerts set.
const AdminStaffAlerts = ({ adminStaffAlerts, isLoading, selectedCustomer, refreshData }) => {
  const [savingId, setSavingId] = useState(null);

  const setSubscription = async (staff, subscribed) => {
    setSavingId(staff.id);
    const properties = {
      createNew: false,
      staffId: staff.id,
      isCustomer: false,
      name: staff.name,
      phone: staff.phone,
      email: staff.email,
      alertSettings: subscribed ? "usePresetAdminAlerts" : "customAlerts",
    };
    if (!subscribed) properties.newAlerts = [];
    try {
      await saveSupportAlerts(selectedCustomer, properties);
      notification.success({
        message: subscribed ? "Subscribed to preset admin alerts." : "Unsubscribed from admin alerts.",
      });
      refreshData();
    } catch (error) {
      notification.error({ message: "Could not update the admin alert subscription. Please try again." });
    } finally {
      setSavingId(null);
    }
  };

  const formatAlerts = (alerts) =>
    alerts.length ? (
      alerts.map((alert, i) => <p key={i}>{capitalizeString(splitSnakeCase(alert.alert_class))}</p>)
    ) : (
      <span>Not subscribed</span>
    );

  const destinationFor = (staff) => {
    if (staff.slack_channel) return "Slack: " + staff.slack_channel;
    return staff.phone || staff.email || "—";
  };

  const formattedRows = adminStaffAlerts.map((staff) => ({
    key: staff.id,
    recipient: staff.name,
    destination: destinationFor(staff),
    alert_description: formatAlerts(staff.support_alerts),
    subscribed: (
      <Switch
        checked={staff.support_alerts.length > 0}
        loading={savingId === staff.id}
        disabled={savingId !== null}
        onChange={(checked) => setSubscription(staff, checked)}
      />
    ),
  }));

  return (
    <>
      <StyledInfoBox>
        Subscribe internal DoorDash staff to the recommended preset admin alerts for this venue, or unsubscribe them.
        These recipients are managed by DoorDash and are only visible to admins.
        <p>
          <i>
            Tip: The switch subscribes admin staff members to the recommended preset alerts for this customer. Need a
            new admin member added? Email {BBOT_SUPPORT_EMAIL}.
          </i>
        </p>
      </StyledInfoBox>
      <BbotTable
        id={"admin-staff-alerts-table"}
        data={formattedRows}
        columns={["Recipient", "Destination", "Alert Description", "Subscribed"]}
        loading={isLoading}
        interactive={false}
        pagination={adminStaffAlerts.length > 10}
      />
    </>
  );
};

export default AdminStaffAlerts;

const StyledInfoBox = styled.div`
  font-size: 14px;
  color: var(--color-neutral__placeholder);
  margin-bottom: 24px;
`;
